import * as React from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action, AdditionalCriteriaType } from 'app/pages/DiscountEditor/actionTypes';
import { setAddOrEditCriteriaModal, saveBookingSystemCriteria } from 'app/pages/DiscountEditor/actions';
import { getAddOrEditModal, getBookingSystemCriteria } from 'app/pages/DiscountEditor/modules/AdditionalCriteria';
import { Modal, Button, Checkbox } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import ScrollPanel from 'app/shared/ui/ScrollPanel';
import { getAllBookingSystems } from 'app/pages/DiscountEditor/modules/RefData';
import { BookingSystem } from 'app/shared/model';
import { SavedBookingSystemCriteria } from 'app/pages/DiscountEditor/modules/AdditionalCriteria/state';

type BookingSystemSelection = {
    id: string,
    name: string,
    selected: boolean
}

interface StateProps {
    isOpen: boolean 
    allBookingSystems: BookingSystem[]
    selections: BookingSystemSelection[]
}

interface DispatchProps { 
    onClose: () => void, 
    onSave: (criteria: SavedBookingSystemCriteria) => void
}

type InternalProps = StateProps & DispatchProps;

interface InternalState {
    selections: BookingSystemSelection[]
    formStatus: FormStatus
}

interface FormStatus {
    isValid: boolean, 
    errorMessage: string|undefined
}

class BookingSystemCriteriaModal extends React.Component<InternalProps, InternalState> {
    constructor(props: InternalProps) {
        super(props);
        this.state = {
            selections: props.selections,
            formStatus: {
                isValid: true, 
                errorMessage: undefined
            }
        }
    }

    componentDidUpdate(prevProps: InternalProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.setState({
                selections: this.props.selections,
                formStatus: {
                    isValid: true,
                    errorMessage: undefined
                }
            });
        }
    }

    private changeSelection(id: string, isSelected: boolean) {
        const selections = this.state.selections.slice(); 
        const selection = selections.find(x => x.id === id); 
        if (selection !== undefined) {
            selection.selected = isSelected; 
            this.setState({selections: selections}); 
        }           
    }

    private save() {
        let isValid = true; 
        let errorMessage = undefined; 
        const selected = this.state.selections.filter(x => x.selected); 

        if (selected.length == 0) {
            isValid = false; 
            errorMessage = "Please select at least one booking system"
        }

        this.setState({
            formStatus: {
                isValid: isValid, 
                errorMessage: errorMessage
            }
        }); 

        if (!isValid) {
            return; 
        }

        const selectedBookingSystems = this.props.allBookingSystems
            .filter(x => selected.some(y => y.id === x.id)); 

        this.props.onSave({
            hasCriteria: true, 
            selectedSystems: selectedBookingSystems
        }); 
    }

    render() {
        return <Modal 
                    open={this.props.isOpen} 
                    onCancel={this.props.onClose}
                    title="Allowed booking systems"
                    footer={[
                        <Button key="cancel" onClick={this.props.onClose}>Cancel</Button>,
                        <Button key="save" type="primary" icon={<CheckOutlined />} onClick={e => this.save()}>Save</Button>
                    ]}>
                {!this.state.formStatus.isValid && <div className="ui message error">
                    <p>{this.state.formStatus.errorMessage}</p>
                </div>}
                <p>Please select the systems that the discount will apply to</p>               
                <ScrollPanel height="150px" className="checkbox-list">
                    {this.state.selections.map(c => 
                        <Checkbox 
                            key={c.id}
                            checked={c.selected}
                            onChange={(e) => this.changeSelection(c.id, e.target.checked)}>
                            {c.name}
                        </Checkbox>
                    )}
                </ScrollPanel> 
            </Modal>
    }
}

const mapStateToProps = (state:any, ownProps: {}) : StateProps => {
    const currentModal = getAddOrEditModal(state);
    const allBookingSystems = getAllBookingSystems(state); 
    const criteria = getBookingSystemCriteria(state);  

    const selectedSystems = criteria.hasCriteria
        ? criteria.selectedSystems
        : allBookingSystems

    const selections : BookingSystemSelection[] = allBookingSystems.map(x => {
        const isSelected = selectedSystems.find(y => String(y.id) === String(x.id)) !== undefined
        return {
            id: x.id, 
            name: x.name, 
            selected: isSelected
        }
    }); 

    return {
        isOpen: currentModal == AdditionalCriteriaType.BookingSystem, 
        allBookingSystems: allBookingSystems,
        selections: selections
    }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>) : DispatchProps =>  {
    return {
        onClose: () => dispatch(setAddOrEditCriteriaModal(AdditionalCriteriaType.None)), 
        onSave: (criteria) => dispatch(saveBookingSystemCriteria(criteria))
    }; 
  }

export default connect(mapStateToProps, mapDispatchToProps)(BookingSystemCriteriaModal);